import { tracking } from '@iwoca/frontend-tracking-library';
import {
  GetApplicationResponse,
  GetProfileResponse,
} from '@iwoca/lapi-client/edge';

import {
  AMPLITUDE_USER_PROPERTIES,
  getAmplitudeDeviceCategory,
  getAmplitudeDomainCountry,
  getAmplitudeSite,
  getAmplitudeSplitTestsFromContext,
} from './amplitudeEvents';
import { getRuntimeEnvironment } from './getRuntimeEnvironment';
import { TGetContext } from '../types';

export const initialiseTracking = () => {
  const runtimeEnvironment = getRuntimeEnvironment();

  tracking.initialise({
    environment:
      runtimeEnvironment === 'production' ? 'production' : 'development',
  });
};

export const identifyTrackingProperties = ({
  context,
  profile,
  application,
  isDirectCustomer,
}: {
  context?: TGetContext['data'];
  profile?: GetProfileResponse['data'];
  application?: GetApplicationResponse['data'];
  isDirectCustomer?: boolean;
}) => {
  const stateKey = context?.state_key;
  const userType = profile?.user_type;

  // Don't identify staff users
  if (profile && profile.impersonator_type) {
    return;
  }

  // Early return if no state key
  if (!context) return;

  // @ts-expect-error expect DD RUM to exist on window
  if (window.DD_RUM) {
    // @ts-expect-error expect DD RUM to exist on window
    window.DD_RUM.setUser({
      state_key: stateKey,
    });
  }

  tracking.ampli.identify(stateKey, {
    site: getAmplitudeSite(),
    Project: 'Partner signup',
  });

  tracking.ampli.identify(stateKey, {
    site: getAmplitudeSite(),
    'Domain country': getAmplitudeDomainCountry(),
    'Device category': getAmplitudeDeviceCategory(),
    // @ts-expect-error Amplitude accepts this as it is
    'Split tests': getAmplitudeSplitTestsFromContext(context),
    ai_company_question_signup_split_test: context.user_split_tests.find(
      (item) => 'ai_company_question_signup' in item,
    )?.ai_company_question_signup,
    open_banking_shareable_link_test: context.user_split_tests.find(
      (item) => 'open_banking_shareable_link_test' in item,
    )?.open_banking_shareable_link_test,
    trading_history_revamp: context.user_split_tests.find(
      (item) => 'trading_history_revamp' in item,
    ),
  });

  if (userType) {
    tracking.ampli.identify(stateKey, {
      site: getAmplitudeSite(),
      userType: userType,
    });
  }

  const introducerID = application?.introducer?.introducer_id;
  if (introducerID) {
    tracking.ampli.identify(stateKey, {
      site: getAmplitudeSite(),
      customerType: 'introducer-referred',
    });
  } else {
    tracking.ampli.identify(stateKey, {
      site: getAmplitudeSite(),
      customerType: isDirectCustomer ? 'direct' : 'partner',
    });
  }

  const requestedAmount = application?.requested_products?.[0]?.amount;

  const suggestedAmount =
    application?.requested_products?.[0]?.suggested_product?.amount;

  const requestedProduct = application?.requested_products?.[0]?.product_type;

  const suggestedProduct =
    application?.requested_products?.[0]?.suggested_product?.product_type;

  tracking.ampli.identify(stateKey, {
    site: getAmplitudeSite(),
    [AMPLITUDE_USER_PROPERTIES.REQUESTED_AMOUNT]: requestedAmount,
    [AMPLITUDE_USER_PROPERTIES.SUGGESTED_AMOUNT]: suggestedAmount,
    [AMPLITUDE_USER_PROPERTIES.REQUESTED_PRODUCT]: requestedProduct,
    [AMPLITUDE_USER_PROPERTIES.SUGGESTED_PRODUCT]: suggestedProduct,
  });

  const introducerCompanyName = application?.introducer?.company?.name;
  if (introducerCompanyName) {
    tracking.ampli.identify(stateKey, {
      site: getAmplitudeSite(),
      introducerAccount: introducerCompanyName,
    });
  }
};
